import { Close as CloseIcon } from '@mui/icons-material'
import { Button, Chip, Dialog, IconButton } from '@mui/material'
import { ReactNode } from 'react'
import { MicroLearningScore } from '../../../../api/models'
import styles from './MicroLearningActionModal.module.css'

export type ModalTheme = 'green' | 'gray' | 'amber' | 'danger' | 'warning'

export type MicroLearningActionModalProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  confirmText: string
  confirmDisabled?: boolean
  theme: ModalTheme
  headerIcon: ReactNode
  title: string
  subtitle: string
  mlId?: string
  mlTitle?: string
  score?: MicroLearningScore | null
  children: ReactNode
  warningBanner?: ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  showMlCard?: boolean
}

export default function MicroLearningActionModal({
  open,
  onClose,
  onConfirm,
  confirmText,
  confirmDisabled = false,
  theme,
  headerIcon,
  title,
  subtitle,
  mlId,
  mlTitle,
  score,
  children,
  warningBanner,
  maxWidth = 'sm',
  showMlCard = true,
}: MicroLearningActionModalProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth slotProps={{ paper: { sx: { borderRadius: '16px' } } }}>
      <div className={styles.modalContainer}>
        <div className={styles.header} data-theme={theme}>
          <div className={styles.headerLeft}>
            <div className={styles.iconCircle}>{headerIcon}</div>
            <div className={styles.headerText}>
              <span className={styles.headerTitle}>{title}</span>
              <span className={styles.headerSubtitle}>{subtitle}</span>
            </div>
          </div>
          <IconButton aria-label='close' onClick={onClose} className={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className={styles.body}>
          {warningBanner}

          {showMlCard && (
            <div className={styles.mlCard}>
              <div className={styles.mlCardHeader}>
                <span className={styles.mlId}>{mlId}</span>
                {score?.total != null && (
                  <Chip
                    size='small'
                    label={`Score: ${score.total}`}
                    className={styles.scoreBadge}
                    data-tier={score.tier}
                  />
                )}
              </div>
              <p className={styles.mlTitle}>{mlTitle}</p>
            </div>
          )}

          {children}
        </div>

        <div className={styles.footer}>
          <Button variant='text' onClick={onClose} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={styles.confirmButton}
            data-theme={theme}>
            {confirmText}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
